
import React, { useState, useRef } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    FormControlLabel,
    Switch,
    InputLabel,
    TextField,
    Popover,
    Button
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses'
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import {useTheme} from '@mui/material/styles';
import {BaseRecordModel,
        InputFieldElementType,
        InputFieldScreenElementModel} from '../../models/modelsFlow';
import {checkLayoutFieldRequirement} from '../../utils/fieldValidations';


import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField";
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { TimeClock } from '@mui/x-date-pickers/TimeClock';
import { TimeView } from '@mui/x-date-pickers';
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { formatDate, formatTime, formatDatetime } from "../../utils/dateFunctions";
import dayjs from 'dayjs';
import {useSnackbar} from "notistack";



export const TextFieldElement: React.FC<{
  element: InputFieldScreenElementModel,
  onChange: (value: string) => void,
}> = ( {
  element,
  onChange
}) => {

  const handleChange = (event: any) => {
    const value = event.target.value;
    if(value) onChange(value)
  }

  const required = true;

  const value = element.value;

  const error = () => {
    if(required && !value) return true
    return false
  };


  return (
      <FormControl
         fullWidth
         variant="filled"
      >
         <TextField
            fullWidth
            error={error()}
            disabled={false}
            multiline={element?.visibleLines  >= 1 ? true : false}
            rows={element?.visibleLines | 1}
            required={required}
            size="small"
            label={`${element.label} (max. ${element?.maxLength})`}
            variant="filled"
            value={value}
            onChange={(event) => handleChange(event)}
            inputProps={{
              maxLength: element.maxLength
            }}
         />
      </FormControl>

    );

};


export const BooleanFieldElement: React.FC<{
  element: InputFieldScreenElementModel,
  onChange: (value: boolean) => void,
}> = ( {
  element,
  onChange
}) => {

  const handleChange = (event: any) => {
    const value=event.target.checked
    onChange(value)
  }

  const initValue = (): boolean => {
    if(element.value === undefined) return false
    if(element.value === null) return false
    if(element.value) return true
    return false
  }

  const value = initValue();


  return (

      <FormControl
         fullWidth
         variant="filled">

         <FormControlLabel
            label={element.label}
            control={
              <Switch
                 size="small"
                 checked={value}
                 onChange={(event) => handleChange(event)}
                 disabled={false}
              />
            }
            labelPlacement="top"
         />
       </FormControl>
    );

};




export const IntegerFieldElement: React.FC<{
  element: InputFieldScreenElementModel,
  onChange: (value: number) => void,
}> = ( {
  element,
  onChange
}) => {

  const required = true;
  //number of digits left and right if the comma
  const stepSize = 1

  const value = element.value

  const error = () => {
    if(required && !value) return true
    return false
  };

  const handleChange = (event: any) => {
    const value = event.target.value;
    onChange(value);
  }

  return (
      <FormControl
         fullWidth
         variant="filled"
      >
      <TextField
         fullWidth
         error={error()}
         disabled={false}
         size="small"
         label={element.label}
         variant="filled"
         type="number"
         value={value}
         onChange={(event) => handleChange(event)}
         InputProps={{
           inputProps: {type: 'number',
                        step: stepSize,
                        min: 0,
                        inputMode: "numeric",
                        pattern: "[0-9]",
                      },
          }}
      />
      </FormControl>

    );

};



const DateTimeFieldElement: React.FC<{
  element: InputFieldScreenElementModel,
  onChange: (value: Date) => void
}> = ( {
  element,
  onChange
}) => {

  const required = true;

  const initValue = () => {
    if(typeof element.value !== "boolean" && element.value) return new Date(element.value)
    return new Date()
  }

  const error = () => {
    if(required && !value) return true
    return false
  };

  const value = initValue()

  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(initValue());
  const [timeView, setTimeView] = useState<TimeView>('hours');
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const openCalendar = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
  }

  const closeCalendar = () => {
    setOpen(false);
    const value = new Date(selectedDate);
    onChange(value);
  }

  const changeTimeView = () => {
    setTimeView('hours');
  }

  const changeDate = (date: Date) => {
    const value = new Date(date);
    setSelectedDate(value);
  }

  const changeTime = (date: Date) => {
    if(timeView === 'hours'){
      setTimeView('minutes')
    }
    const value = new Date(date);
    setSelectedDate(value);
  }

  const handleChange = (newCalendarValue: any) => {
    const value = new Date(newCalendarValue);
    onChange(value);
  }

  return (
    <>
        <Box ref={anchorRef}>
         <DateTimeField
            disabled={true}
            variant="filled"
            fullWidth
            label={element.label}
            value={dayjs(value)}
            format="DD.MM.YYYY HH:mm"
            onChange={(newCalendarValue: any) => handleChange(newCalendarValue)}
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={(event) => openCalendar(event)}>
                    <ScheduleIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          </Box>

          <Popover
            open={open}
            anchorEl={anchorRef.current}
            onClose={closeCalendar}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <Box>

              <DateCalendar
                value={dayjs(selectedDate)}
                onChange={(newCalendarValue: any) => changeDate(newCalendarValue)}
              />

              <Box
               sx={{display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"}}
              >
                <Typography>
                  {formatTime(selectedDate)}
                </Typography>

                <IconButton
                   size="small"
                   disabled={timeView==='hours'}
                   onClick={() => changeTimeView()}>
                    <ArrowBackIcon />
                </IconButton>
              </Box>

              <TimeClock
                ampm={false}
                view={timeView}
                views={['hours', 'minutes']}
                value={dayjs(selectedDate)}
                onChange={(newCalendarValue: any) => changeTime(newCalendarValue)}
              />
            </Box>
            <Box
               sx={{display: "flex",
                    justifyContent: "flex-end"}}
            >
              <Button
                 size="small"
                 onClick={() => closeCalendar()}
                 variant='contained'
              >
                  <CheckIcon />
              </Button>
            </Box>
          </Popover>
    </>
    );

};



const FileFieldElement: React.FC<{
  element: InputFieldScreenElementModel,
  onChange: (value: File) => void
}> = ( {
  element,
  onChange
}) => {

  const required = true;

  const initValue = () => {
    if(element.value) return element.value
    return ""
  }

  const error = () => {
    if(required && !value) return true
    return false
  };

  const value = initValue()

  const handleChange = (event: any) => {
    const value = event.target.files[0]
    if(!value) return;
    onChange(value)
  }

  const acceptExtensions = element.allowedExtensions.join(',')
  const label = `${element.label} (${acceptExtensions})`

  return (

      <TextField
         fullWidth
         error={error()}
         disabled
         rows={1}
         required={true}
         size="small"
         label={label}
         variant="filled"
         value={value}
         InputProps={{
           disableUnderline: true,
           endAdornment: (
              <InputAdornment position="end">
                 <IconButton size="small" component="label">
                 <FileUploadIcon/>
                 <input
                    type="file"
                    accept={acceptExtensions}
                    hidden
                    onChange={(e) => handleChange(e)}
                  />
                </IconButton>
            </InputAdornment>
        ),
         }}
      />
    )
};



export const InputFieldElementComponent: React.FC<{
  element: InputFieldScreenElementModel,
  onChange: (value: InputFieldElementType, element: InputFieldScreenElementModel, file: File | null ) => void,
}> = ( {
  element,
  onChange
}) => {

  const { enqueueSnackbar } = useSnackbar();

  const validateFile = (file: File, allowedExtensions: string[]) => {
    //check if file is valid
    const thisExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    return allowedExtensions.includes(thisExtension)
  }

  const handleChangeTextField = (newValue: string) => {
    onChange(newValue, element, null)
  }

  const handleChangeDateTimeField = (newValue: Date) => {
    const newValueString = formatDatetime(newValue, {variant: 'A'})
    onChange(newValueString, element, null)
  }

  const handleChangeIntegerField = (newValue: number) => {
    onChange(newValue, element, null)
  }

  const handleChangeBooleanField = (newValue: boolean) => {
    onChange(newValue, element, null)
  }

  const handleChangeFileField = (newValue: File) => {
    const isValidFile = validateFile(newValue, element.allowedExtensions)

    if(isValidFile){
      onChange(newValue.name, element, newValue)
    } else {
      enqueueSnackbar('Invalid file extension', {
        variant: "error",
      });
    }
  }


  switch(element.type){
    case "DateTimeField":
      return (
         <DateTimeFieldElement
           element={element}
           onChange={handleChangeDateTimeField}
         />
      )
    case "FileField":
      return (
         <FileFieldElement
           element={element}
           onChange={handleChangeFileField}
         />
      )
    case "TextField":
       return (
         <TextFieldElement
           element={element}
           onChange={handleChangeTextField}
         />
       )
    case "IntegerField":
       return (
         <IntegerFieldElement
           element={element}
           onChange={handleChangeIntegerField}
         />
       )
    case "BooleanField":
       return (
         <BooleanFieldElement
           element={element}
           onChange={handleChangeBooleanField}
         />
       )
    default:
      return (
        <Typography color="error">
          Error: Element Type {element.type} not defined yet
        </Typography>
      )
  }
}
